/**
 * Page-level UDA scope for the **Leases & Agreements dashboard** (and the Agreements
 * list, which is both a route of its own and the dashboard's first tab).
 *
 * Everywhere else in the app a State/DTCP user switches UDA from the GLOBAL HEADER
 * selector, which re-issues the JWT with a new tenant. This module deliberately does
 * NOT follow that: the dashboard carries its own dropdown, sitting on the Agreements
 * list toolbar, and the header selector is disabled while these screens are open (see
 * isLamsUdaScopedRoute below, wired into components/Header.tsx). One pick re-scopes the
 * whole dashboard — the KPI tiles, the DCB cards, the portfolio charts and the list —
 * without re-authenticating the session or losing the page.
 *
 * HOW IT REACHES THE SERVER. Every LAMS request carries the picked UDA in the
 * `x-lams-uda` header, injected by the interceptor below, and the server's
 * resolveUdaId (services/LamsUdaScopeService.ts) reads it. A header rather than a query
 * param because the LAMS controllers run validate_input_params over req.query and 400
 * the whole request on any key they do not declare — a param would have to be declared
 * in every whitelist in the module. The consequence worth knowing: individual screens
 * did not have to be rewritten to thread a scope down, so a component's own fetch is
 * scoped automatically — but React does not see the change, so anything that must
 * refetch has to depend on `udaId` (or be remounted with it as a `key`).
 *
 * NARROWING ONLY. A user whose JWT already pins them to a UDA ignores this entirely,
 * server-side: the token wins. The dropdown is therefore shown to state users only;
 * everyone else sees their own UDA as a static chip.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Skeleton } from '@/components/base/Skeleton';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { InputAdornment, Popover, TextField } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axiosInstance from '../../../../../services/interceptor';

/** Header the picked UDA rides in. Must match SCOPE_UDA_HEADER on the server. */
export const LAMS_UDA_HEADER = 'x-lams-uda';

/** What "no UDA picked" is called — every UDA's rows, the state user's default view. */
export const ALL_UDAS_LABEL = 'All UDAs';

interface LamsUdaScopeState {
  /** ds_uda_lst_t.uda_id for the picked UDA; null = every UDA (state level). */
  udaId: number | null;
  /** Display name of the pick, so the button can label itself before options load. */
  udaName: string | null;
  /** False until the user (or the one-time seed below) has actually chosen. */
  touched: boolean;
  setScope: (udaId: number | null, udaName: string | null) => void;
  seed: (udaId: number | null, udaName: string | null) => void;
}

/**
 * Persisted so the pick survives moving between the dashboard and the standalone list,
 * and a page reload. Intentionally SEPARATE from the auth tenant — nothing here
 * re-issues the JWT.
 */
export const useLamsUdaScopeStore = create<LamsUdaScopeState>()(
  persist(
    (set) => ({
      udaId: null,
      udaName: null,
      touched: false,
      setScope: (udaId, udaName) => set({ udaId, udaName, touched: true }),
      seed: (udaId, udaName) =>
        set((prev) => (prev.touched ? prev : { udaId, udaName, touched: true })),
    }),
    { name: 'lams-uda-scope' }
  )
);

/**
 * Routes that own their UDA scope and therefore IGNORE the global header selector.
 * Header.tsx disables its own dropdown for these paths so the two cannot disagree, and
 * the interceptor below only stamps requests made from them — so the other LAMS screens
 * (DCB, disputes, renewals, ...) keep following the header exactly as before.
 */
export function isLamsUdaScopedRoute(pathname: string): boolean {
  const p = (pathname || '').toLowerCase().replace(/\/+$/, '');
  const LAMS = '/maud-erp/leases-and-agreements';
  // The dashboard is the index route, so it is matched by the bare module path as well
  // as by an explicit /dashboard. Everything else is a submodule that carries the
  // picker in its own header bar.
  if (p.endsWith(LAMS) || p.endsWith(`${LAMS}/dashboard`)) return true;
  // Reports is a SUBTREE (reports/333, reports/489, ...) and every LAMS report's SQL
  // already carries the $$uda_id$$ placeholder, so the whole subtree is scoped.
  if (p.includes(`${LAMS}/reports`)) return true;
  return SCOPED_SUBROUTES.some((r) => p.endsWith(`${LAMS}/${r}`));
}

/**
 * The submodules that carry their own UDA picker, and therefore ignore the header
 * selector. Adding a screen here is only half the job — the page must also render
 * <LamsUdaScopeSelect> and depend on `udaId` in its fetches, or it will silently keep
 * showing the previous UDA's rows with no way to change them.
 */
const SCOPED_SUBROUTES = [
  'list',
  'agreements',
  'service-requests',
  'renewals',
  'cancellations',
  'data-entry',
  'dcb',
  'calendar',
  // Added 2026-10-08: these screens now carry the picker in their header too, and
  // their backends already narrow by resolveUdaId (x-lams-uda for a state user).
  'defaulters',
  'notices',
  'documents',
  'judgements',
  'objections',
  'remissions',
  'challans',
  'transfers',
  'disputes',
];

/**
 * Setup owns NO UDA at all: not the header selector, and not a picker of its own.
 *
 * The LAMS masters are setup data, not per-UDA transaction rows — four of the six
 * tables have no uda_id column, and of the two that do, only
 * dslams_agreement_template reads it. Rather than offer a control that narrows almost
 * nothing, these screens carry no UDA control and resolve the UDA from the JWT, as
 * they always did.
 *
 * They are still listed here because the HEADER selector must stay disabled on them
 * (below) — but they are deliberately NOT scoped routes, so the interceptor sends no
 * x-lams-uda and a UDA picked on the dashboard never silently re-scopes a master list.
 */
const SETUP_ROUTES = ['setup', 'agreement-types', 'agreement-templates'];

/**
 * Routes where the GLOBAL header UDA selector must be disabled.
 *
 * Wider than {@link isLamsUdaScopedRoute}: it also covers Setup, which answers to no
 * UDA at all. The two are separate predicates precisely because "do not take the UDA
 * from the header" and "take the UDA from this page's picker" are different claims —
 * Setup makes the first and not the second.
 */
export function isLamsHeaderUdaDisabledRoute(pathname: string): boolean {
  if (isLamsUdaScopedRoute(pathname)) return true;
  const p = (pathname || '').toLowerCase().replace(/\/+$/, '');
  const LAMS = '/maud-erp/leases-and-agreements';
  return SETUP_ROUTES.some((r) => p.includes(`${LAMS}/${r}`));
}

/*
 * Stamp every LAMS call made from a UDA-scoped route with the picked UDA.
 *
 * Registered once, at module load — this file is imported for its side effect by the
 * module's route config, so the header is attached even on a hard reload straight onto
 * the dashboard, before any component of ours has rendered.
 *
 * Gated on the CURRENT PATH rather than on the store alone: the module stays loaded
 * after the reader navigates away, and silently re-scoping the DCB or disputes screens
 * — whose UDA still comes from the header selector — would leave the two disagreeing.
 */
declare global {
  // eslint-disable-next-line no-var
  var __lamsUdaInterceptorInstalled: boolean | undefined;
}

if (!globalThis.__lamsUdaInterceptorInstalled) {
  globalThis.__lamsUdaInterceptorInstalled = true;
  axiosInstance.interceptors.request.use((config) => {
    const url = config.url || '';
    const isLamsCall =
      url.startsWith('lams/') || url.startsWith('/lams/') || url.includes('/apiv1/lams/');
    if (!isLamsCall) return config;
    if (!config.headers) return config;
    // A create form sets this itself, from the UDA the operator picked in the form —
    // the page scope must not overwrite a deliberate per-request choice.
    if (config.headers[LAMS_UDA_HEADER]) return config;
    if (!isLamsUdaScopedRoute(window.location.pathname)) return config;
    const { udaId } = useLamsUdaScopeStore.getState();
    if (udaId) config.headers[LAMS_UDA_HEADER] = String(udaId);
    return config;
  });
}

/** A UDA option, normalised out of ds_dtcp.ds_uda_lst_t. */
export interface LamsUdaOption {
  uda_id: number;
  uda_nm: string;
  uda_cd?: string;
  tenant_code?: string;
}

export interface LamsUdaScope {
  /**
   * Whether this scope applies where it is being rendered.
   *
   * The LAMS master screens are mounted under SIX modules' `setup` route (asset
   * management, finance, legal cases, municipal council, ...), not just this one. On
   * those foreign paths the interceptor sends no header and the global selector is
   * still live, so the picker must not appear and must not disable anything — the
   * screen has to behave exactly as it did before this scope existed.
   */
  active: boolean;
  /** True when the logged-in user is a State/DTCP user — the only one who may switch. */
  isStateUser: boolean;
  /** The UDA in force, or null for "every UDA". Use it as a fetch dependency. */
  udaId: number | null;
  /** Label of the UDA in force, for headings and chips. */
  udaLabel: string;
  options: LamsUdaOption[];
  loading: boolean;
  setScope: (udaId: number | null, udaName: string | null) => void;
}

/**
 * The UDA these screens should operate on.
 *
 * State users: whatever they picked here (NOT authUser.tenantId).
 * Everyone else: their own UDA, fixed by the JWT — the store is not consulted, because
 * the server would ignore it anyway.
 */
export function useLamsUdaScope(): LamsUdaScope {
  const { pathname } = useLocation();
  const active = isLamsUdaScopedRoute(pathname);
  const { user: authUser } = useAuth();
  const isStateUser = (authUser as any)?.isStateRegionalUser || false;
  const authTenants = (authUser as any)?.tenants || [];
  const allowedCodesKey = authTenants
    .map((t: any) => String(t?.code ?? t?.tenant_code ?? ''))
    .filter(Boolean)
    .sort()
    .join(',');

  const udaId = useLamsUdaScopeStore((s) => s.udaId);
  const udaName = useLamsUdaScopeStore((s) => s.udaName);
  const touched = useLamsUdaScopeStore((s) => s.touched);
  const setScope = useLamsUdaScopeStore((s) => s.setScope);
  const seed = useLamsUdaScopeStore((s) => s.seed);

  const [options, setOptions] = useState<LamsUdaOption[]>([]);
  const [loading, setLoading] = useState(false);

  /*
   * The UDA master, filtered to the user's own jurisdiction. Same source and same
   * jurisdiction filter as the header selector, so the two lists can never offer
   * different UDAs. Only state users need it — everyone else has exactly one.
   */
  useEffect(() => {
    if (!isStateUser) return;
    let cancelled = false;
    setLoading(true);
    const allowed = new Set(allowedCodesKey ? allowedCodesKey.split(',') : []);
    axiosInstance
      .get('erp-admin/uda')
      .then((res) => {
        if (cancelled) return;
        const list: any[] = res.data?.data || res.data || [];
        const opts: LamsUdaOption[] = (Array.isArray(list) ? list : [])
          .filter((u: any) => u?.uda_id != null)
          .map((u: any) => ({
            uda_id: Number(u.uda_id),
            uda_nm: u.uda_nm,
            uda_cd: u.uda_cd,
            tenant_code: u.tenant_code != null ? String(u.tenant_code) : undefined,
          }));
        // Fall back to the full list when the account has no jurisdiction tenants
        // configured (admin / test logins), matching useUdaOptions.
        setOptions(
          allowed.size > 0
            ? opts.filter((o) => (o.tenant_code ? allowed.has(o.tenant_code) : false))
            : opts
        );
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isStateUser, allowedCodesKey]);

  /*
   * First visit: start on whatever UDA the session is already in, so a state admin who
   * picked a UDA in the header before opening the dashboard does not land on a view of
   * every UDA instead. Runs once — after that the dropdown is the only input.
   */
  const authUdaId = Number((authUser as any)?.udaId);
  useEffect(() => {
    if (touched || !isStateUser) return;
    if (!Number.isFinite(authUdaId) || authUdaId <= 0) return;
    const hit = options.find((o) => o.uda_id === authUdaId);
    seed(authUdaId, hit?.uda_nm ?? null);
  }, [touched, isStateUser, authUdaId, options, seed]);

  // A non-state user is pinned by their token, so report THAT, not the stored pick.
  const effectiveUdaId = isStateUser
    ? udaId
    : Number.isFinite(authUdaId) && authUdaId > 0
      ? authUdaId
      : null;

  const udaLabel = useMemo(() => {
    if (!isStateUser) {
      return (
        (authUser as any)?.tenants?.find((t: any) => t.code === (authUser as any)?.tenantId)?.name ||
        (authUser as any)?.tenantId ||
        'My UDA'
      );
    }
    if (!udaId) return ALL_UDAS_LABEL;
    return options.find((o) => o.uda_id === udaId)?.uda_nm || udaName || ALL_UDAS_LABEL;
  }, [isStateUser, udaId, udaName, options, authUser]);

  return {
    active,
    isStateUser,
    udaId: effectiveUdaId,
    udaLabel,
    options,
    loading,
    setScope,
  };
}

interface LamsUdaScopeSelectProps {
  scope: LamsUdaScope;
  /**
   * Where the control is sitting, which is all that changes about it:
   *   'bar'    - a slate/white chip for a light filter bar (the agreements list).
   *   'header' - a translucent white chip for the violet gradient page headers the
   *              submodule screens carry (DCB, renewals, cancellations, ...).
   */
  variant?: 'bar' | 'header';
  className?: string;
}

/**
 * The dropdown itself — a compact toolbar control, sized for the Agreements list's
 * filter bar rather than a form field. State users get a searchable list of every UDA
 * in their jurisdiction plus "All UDAs"; everyone else gets a static chip, because
 * their one UDA is not a choice.
 */
export function LamsUdaScopeSelect({ scope, variant = 'bar', className }: LamsUdaScopeSelectProps) {
  const { active, isStateUser, udaId, udaLabel, options, loading, setScope } = scope;
  const onHeader = variant === 'header';
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        String(o.uda_nm || '').toLowerCase().includes(q) ||
        String(o.uda_cd || '').toLowerCase().includes(q)
    );
  }, [options, query]);

  const pick = useCallback(
    (next: LamsUdaOption | null) => {
      setScope(next ? next.uda_id : null, next ? next.uda_nm : null);
      setAnchorEl(null);
      setQuery('');
    },
    [setScope]
  );

  // Not this screen's scope — the global header selector is in charge here.
  if (!active) return null;

  if (!isStateUser) {
    return (
      <span
        className={
          className ??
          (onHeader
            ? 'flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 text-white text-[11px] font-medium flex-shrink-0'
            : 'flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-600 flex-shrink-0')
        }
        title="Your UDA"
      >
        <i className={onHeader ? 'ri-building-2-line' : 'ri-building-2-line text-slate-400'}></i>
        <span className="truncate max-w-[160px]">{udaLabel}</span>
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={!!anchorEl}
        title="Choose the UDA this dashboard works on"
        className={
          className ??
          (onHeader
            ? 'flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25 text-white text-[11px] font-medium transition-colors flex-shrink-0'
            : `flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors flex-shrink-0 ${
                udaId
                  ? 'bg-violet-50 border-violet-300 text-violet-800'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
              }`)
        }
      >
        <i className="ri-building-2-line"></i>
        <span className="truncate max-w-[180px]">{udaLabel}</span>
        <i className={anchorEl ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
      </button>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setQuery('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 2, width: 'min(320px, calc(100vw - 32px))' } } }}
      >
        <div className="p-2">
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search UDA..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <i className="ri-search-line text-gray-400"></i>
                  </InputAdornment>
                ),
              },
            }}
          />
        </div>

        <div className="max-h-[320px] overflow-auto pb-2">
          {/* "All UDAs" is not one of the UDAs — it is the absence of one — so it sits
              above the divider rather than in the list. */}
          <button
            type="button"
            onClick={() => pick(null)}
            className={`w-full text-left px-3 py-2 flex items-center gap-2 text-[13px] transition-colors ${
              !udaId ? 'bg-violet-50 text-violet-800 font-semibold' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className="ri-government-line text-gray-400"></i>
            <span className="truncate">{ALL_UDAS_LABEL}</span>
            <span className="ml-auto text-[11px] text-gray-400 flex-shrink-0">State level</span>
            {!udaId && <i className="ri-check-line text-violet-600"></i>}
          </button>

          <div className="my-1 border-t border-gray-100"></div>

          {loading ? (
            <div className="px-3 py-3 space-y-2">
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              {query ? `No UDA matches "${query}".` : 'No UDAs available.'}
            </p>
          ) : (
            filtered.map((o) => {
              const active = o.uda_id === udaId;
              return (
                <button
                  key={o.uda_id}
                  type="button"
                  onClick={() => pick(o)}
                  title={o.uda_nm}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 text-[13px] transition-colors ${
                    active ? 'bg-violet-50 text-violet-800 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="truncate">{o.uda_nm}</span>
                  <span className="ml-auto text-[11px] text-gray-400 flex-shrink-0">{o.uda_cd || ''}</span>
                  {active && <i className="ri-check-line text-violet-600 flex-shrink-0"></i>}
                </button>
              );
            })
          )}
        </div>
      </Popover>
    </>
  );
}

/**
 * Axios config that pins ONE request to a UDA, whatever the page scope says.
 *
 * This is how a create form stamps the row it is about to write, and how it scopes its
 * own agreement search: the operator picks a UDA in the form, and the request carries
 * it in the same header resolveUdaId already reads. Returns an empty config for a null
 * id, so callers can spread it unconditionally.
 */
export function lamsUdaRequest(udaId: number | null | undefined) {
  return udaId ? { headers: { [LAMS_UDA_HEADER]: String(udaId) } } : {};
}

interface LamsUdaFormFieldProps {
  scope: LamsUdaScope;
  value: number | null;
  onChange: (udaId: number | null) => void;
  /** Marks the field required and shows the red asterisk. */
  required?: boolean;
  /** Validation message, shown under the field. */
  error?: string | null;
  label?: string;
  helperText?: string;
}

/**
 * The UDA a record being CREATED will belong to, as a normal form field.
 *
 * Separate from {@link LamsUdaScopeSelect}, which chooses what a screen SHOWS. This one
 * chooses what a row IS — it is answered once, at creation, and the answer is written
 * to uda_id. The two are deliberately not the same control: reading every UDA's
 * agreements is a perfectly ordinary thing to do, but saving one into "every UDA" is
 * not, so the list may be un-narrowed while this field still has to be filled in.
 *
 * Only a state user sees a picker; everyone else is pinned by their JWT, which the
 * server honours over anything sent here, so they get a static read-only line instead.
 */
export function LamsUdaFormField({
  scope,
  value,
  onChange,
  required = true,
  error,
  label = 'UDA',
  helperText,
}: LamsUdaFormFieldProps) {
  const { isStateUser, udaLabel, options, loading } = scope;

  if (!isStateUser) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-600">
          {udaLabel}
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        disabled={loading}
        className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${
          error ? 'border-red-400 bg-red-50' : 'border-gray-300'
        }`}
      >
        <option value="">{loading ? 'Loading UDAs...' : 'Select UDA...'}</option>
        {options.map((o) => (
          <option key={o.uda_id} value={o.uda_id}>
            {o.uda_nm}
          </option>
        ))}
      </select>
      {error ? (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      ) : helperText ? (
        <p className="mt-1 text-xs text-gray-500">{helperText}</p>
      ) : null}
    </div>
  );
}
